import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../../Services/data/data.service";
import { Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import Button from "../../../../Shared/Buttons/Button";
import Input from "../../../../Shared/Input";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import { Download, Plus, Search } from "lucide-react";
import { downloadCsv } from "../../../../../Utils/csvExport";
import ResetFiltersButton from "../../../../Filter/ResetFiltersButton";
import { crudTypes, PriceBundleFilterTypes } from "../../../../../Config/constants";
import PriceBundleTable from "../PriceBundle/PriceBundleTable";
import PriceBundleDialog from "../PriceBundle/PriceBundleDialog";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from "../../../../../hooks/useFilterPersistence";

function PriceBundleTab(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  // General
  const [originalPriceBundles, setOriginalPriceBundles] = useState([]); // Store the original list of price definitions - unfiltered
  const [priceBundleList, setPriceBundleList] = useState([]); // Filtered and sorted
  const [allCompanies, setAllCompanies] = useState([]);
  const { filters, setFilter, setFilterText, resetFilters, getFilterValue, hasActiveFilters, page, setPage, pageSize, setPageSize } = useFilterPersistence({
    pageKey: 'financial-price-bundles',
    filterSchema: { pb_search: PriceBundleFilterTypes.SEARCH, pb_company: PriceBundleFilterTypes.COMPANY },
    defaultPageSize: 25,
    persistPageSize: true,
  });

  // Price Definition Dialog
  const [selectedPriceBundle, setSelectedPriceBundle] = useState(null);
  const [priceBundleDialogOpen, setPriceBundleDialogOpen] = useState(false);
  const [crudType, setCrudType] = useState(null); // Create, Edit, View

  // Fetch Price Definitions
  useEffect(() => {
    dataService
      .getAllPriceBundles(props.Login.userid)
      .then((response) => {
        setOriginalPriceBundles(response);
      })
      .catch((error) => {
        console.error("Error fetching price definitions:", error);
        // Handle error, e.g., show a message to the user
      });
  }, [props.Login.userid]);

  // Fetch Companies
  useEffect(() => {
    if (props.Login && props.Login.userid) {
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
        });
    }
  }, [props.Login.userid]);

  const filterList = (thisFilters, priceBundles) => {
    let varFilteredlist = priceBundles ? priceBundles : [...originalPriceBundles];

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case PriceBundleFilterTypes.SEARCH:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.NAME:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.REFERENCE:
          varFilteredlist = varFilteredlist.filter((x) => x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceBundleFilterTypes.COMPANY:
          varFilteredlist = varFilteredlist.filter((x) => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
          break;
        default:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.reference.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    
    // Sort alphabetically by reference
    varFilteredlist = varFilteredlist.sort((a, b) => a.reference.localeCompare(b.reference));
    
    if (priceBundles) {
      return varFilteredlist;
    } else {
      setPriceBundleList(varFilteredlist);
    }
  };

  // Re-apply filters whenever source data or active filters change (covers reset + URL restore)
  useEffect(() => {
    filterList(filters);
  }, [originalPriceBundles, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create Price Definition Logic
  const openCreatePriceBundleDialog = () => {
    setSelectedPriceBundle(null);
    setCrudType(crudTypes.CREATE);
    setPriceBundleDialogOpen(true);
  };

  const closePriceBundleDialog = () => {
    setPriceBundleDialogOpen(false);
    setCrudType(null);
  };

  // Open the dialog to edit an existing price bundle
  const handleEditBundle = (priceBundleUniqueId) => {
    // Logic to open the dialog for editing an existing price definition
    const priceBundle = originalPriceBundles.find((x) => x.uniqueId === priceBundleUniqueId);
    if (!priceBundle) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceBundle(priceBundle);
    setCrudType(crudTypes.UPDATE);
    setPriceBundleDialogOpen(true);
  };

  // Open the dialog to view a price bundle
  const handleViewBundle = (priceBundleUniqueId) => {
    const priceBundle = originalPriceBundles.find((x) => x.uniqueId === priceBundleUniqueId);
    if (!priceBundle) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceBundle(priceBundle);
    setCrudType(crudTypes.READ);
    setPriceBundleDialogOpen(true);
  };

  // Add this handler to add a new definition
  const handleAddPriceBundle = (newBundle) => {
    setOriginalPriceBundles((prev) => [...prev, newBundle]);
  };

  // Add this handler to update an existing definition
  const handleUpdatePriceBundle = (updatedBundle) => {
    setOriginalPriceBundles((prev) => prev.map((def) => def.uniqueId === updatedBundle.uniqueId ? updatedBundle : def));
  };

  // Handler to remove a deleted bundle from the lists
  const handleDeletePriceBundle = (deletedBundleUniqueId) => {
    setOriginalPriceBundles((prev) => prev.filter((def) => def.uniqueId !== deletedBundleUniqueId));
  };

  // Utility function to highlight search text
  const getSearchValue = () => {
    return getFilterValue(PriceBundleFilterTypes.SEARCH);
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(PriceBundleFilterTypes.COMPANY);
    return allCompanies.find((company) => company.id === parseInt(companyId)) || null;
  };

  const handleExport = () => {
    const headers = [
      t('financialCenter.reference'),
      t('common.name'),
      t('common.description'),
      t('financialCenter.rate'),
      t('common.type'),
      t('financialCenter.account'),
      t('financialCenter.company'),
      t('financialCenter.updatedOn'),
    ];
    const rows = priceBundleList.map((bundle) => {
      const rate = bundle.priceBundleItems?.reduce((sum, item) => (item.isFixed ? sum : sum + item.price * item.multiplier), 0) || 0;
      const fixedRate = bundle.priceBundleItems?.reduce((sum, item) => (item.isFixed ? sum + item.price * item.multiplier : sum), 0) || 0;
      return [
        bundle.reference,
        bundle.name,
        bundle.description || '',
        fixedRate > 0
          ? `${bundle.currencySymbol}${rate.toFixed(3)} + ${bundle.currencySymbol}${fixedRate.toFixed(3)} ${t('priceBundle.fixedSuffix')}`
          : `${bundle.currencySymbol}${rate.toFixed(3)}`,
        bundle.costBundleTypeName || '',
        bundle.account?.name || '',
        bundle.companyName,
        bundle.updatedOn ? new Date(bundle.updatedOn).toLocaleDateString() : '',
      ];
    });
    downloadCsv('price-bundles.csv', headers, rows);
  };

  // Calculate paginated rows
  const paginatedPriceBundles = priceBundleList.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder={t('priceBundleTab.searchPlaceholder')}
              className={classes.search__input}
              type="text"
              value={getFilterValue(PriceBundleFilterTypes.SEARCH)}
              onChange={(e) => setFilterText(PriceBundleFilterTypes.SEARCH, e.target.value)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedCompany()?.id} onChange={(company) => setFilter(PriceBundleFilterTypes.COMPANY, company?.id ?? "")} size="small">
              <SelectTrigger>{getSelectedCompany()?.name || t('priceBundleTab.allCompanies')}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('priceBundleTab.allCompanies')}</SelectItem>
                {allCompanies.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" variant="secondary" startIcon={<Download />} onClick={handleExport}>
            {t('common.export')}
          </Button>
          <Button size="small" startIcon={<Plus />} onClick={() => openCreatePriceBundleDialog()}>
            {t('priceBundleTab.createPriceBundle')}
          </Button>
        </div>
      </div>
      {/* Table */}
      <PriceBundleTable priceBundles={paginatedPriceBundles} handleEditBundle={handleEditBundle} handleViewBundle={handleViewBundle} searchValue={getSearchValue()} />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={priceBundleList.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Dialog */}
      <PriceBundleDialog
        dialogOpen={priceBundleDialogOpen}
        closeDialog={closePriceBundleDialog}
        crudType={crudType}
        priceBundle={selectedPriceBundle}
        onAdd={handleAddPriceBundle}
        onUpdate={handleUpdatePriceBundle}
        onDelete={handleDeletePriceBundle}
        {...props}
      />
    </React.Fragment>
  );
}

export default PriceBundleTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

});
